<script setup>
// SiteUp: filtro rápido de etiquetas no sub-rail da lista de conversas.
// Busca (typeahead) + multi-seleção (OU). Estado vive no ChatList (sessão).
// O painel teleporta para um alvo interno da coluna (nunca body/sidebar).
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  labels: { type: Array, default: () => [] },
  modelValue: { type: Array, default: () => [] },
  teleportTarget: {
    type: String,
    default: '#conversation-label-filter-panel-target',
  },
  // 'button' = pílula só-ícone (uso antigo, no cromo).
  // 'row'    = linha com ícone + rótulo + contagem, pra viver dentro do menu
  //            do funil junto dos outros filtros (Lucas 2026-08-10: "é pra
  //            aparecer o texto... substituir e mesclar o que era antes").
  variant: {
    type: String,
    default: 'button',
  },
  size: {
    type: String,
    default: 'xs',
  },
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const isOpen = ref(false);
const search = ref('');
const triggerRef = ref(null);
const panelRef = ref(null);

const filteredLabels = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return props.labels;
  return props.labels.filter(l => (l.title || '').toLowerCase().includes(q));
});

const isSelected = title => props.modelValue.includes(title);

const toggle = title => {
  const next = isSelected(title)
    ? props.modelValue.filter(labelTitle => labelTitle !== title)
    : [...props.modelValue, title];
  emit('update:modelValue', next);
};

const clearAll = () => emit('update:modelValue', []);

const triggerLabel = computed(() => {
  if (!props.modelValue.length) return t('LABEL_MGMT.HEADER');

  return t('LABEL_MGMT.COUNT', { n: props.modelValue.length });
});

const close = () => {
  isOpen.value = false;
  search.value = '';
};

const onDocClick = e => {
  if (!isOpen.value) return;
  const target = e.target;
  if (triggerRef.value?.contains(target)) return;
  if (panelRef.value?.contains(target)) return;
  close();
};

onMounted(() => document.addEventListener('click', onDocClick));
onBeforeUnmount(() => document.removeEventListener('click', onDocClick));
</script>

<template>
  <div :class="variant === 'row' ? 'w-full' : 'flex justify-center'">
    <div ref="triggerRef" :class="variant === 'row' ? '' : 'relative'">
      <button
        v-if="variant === 'row'"
        type="button"
        data-testid="conversation-label-filter-row"
        class="flex min-h-9 w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors hover:bg-n-alpha-2"
        :class="
          modelValue.length ? 'font-semibold text-n-blue-11' : 'text-n-slate-12'
        "
        :aria-expanded="isOpen"
        aria-controls="conversation-label-filter-panel-inline"
        @click="isOpen = !isOpen"
      >
        <span
          class="i-ph-tag size-4 shrink-0"
          :class="modelValue.length ? 'text-n-blue-11' : 'text-n-slate-11'"
        />
        <span class="min-w-0 flex-1 truncate">{{ triggerLabel }}</span>
        <span
          v-if="modelValue.length"
          class="shrink-0 text-[11px] tabular-nums text-n-blue-11"
        >
          {{ modelValue.length }}
        </span>
        <span
          class="i-ph-caret-down size-4 shrink-0 text-n-slate-10 transition-transform"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>
      <NextButton
        v-else
        v-tooltip.top="triggerLabel"
        icon="i-ph-tag"
        color="teal"
        :class="modelValue.length ? 'ring-1 ring-n-teal-9/50' : ''"
        :data-active="modelValue.length ? 'true' : 'false'"
        :aria-label="triggerLabel"
        :aria-expanded="isOpen"
        aria-controls="conversation-label-filter-panel"
        :size="size"
        faded
        @click="isOpen = !isOpen"
      />
      <span
        v-if="modelValue.length"
        data-testid="conversation-label-filter-count"
        class="pointer-events-none absolute -top-1 min-w-4 rounded-full bg-n-teal-9 px-1 text-center text-[10px] font-semibold leading-4 text-white ltr:-right-1 rtl:-left-1"
      >
        {{ modelValue.length }}
      </span>
    </div>
    <!-- variant "row": abre INLINE, logo abaixo da linha, dentro do próprio
         menu de filtros. O Teleport mandava o painel pro alvo na LISTA de
         conversas — fora do menu — e por isso ele simplesmente não aparecia
         (Lucas 2026-08-10). Inline também é o que ele pediu: clica e as
         etiquetas surgem abaixo, com a busca junto. -->
    <div v-if="variant === 'row' && isOpen" class="mt-1 w-full">
      <div
        id="conversation-label-filter-panel-inline"
        ref="panelRef"
        data-testid="conversation-label-filter-panel-inline"
        class="pointer-events-auto flex w-full min-w-0 flex-col gap-2 rounded-xl border border-n-weak bg-n-slate-2/40 p-2"
        @keydown.esc="close"
      >
        <input
          v-model="search"
          type="text"
          :aria-label="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
          :placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
          class="reset-base !mb-0 min-w-0 h-8 w-full appearance-none rounded-md border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
        />
        <div class="flex max-h-64 flex-col gap-0.5 overflow-auto">
          <label
            v-for="l in filteredLabels"
            :key="l.id"
            class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          >
            <input
              type="checkbox"
              :checked="isSelected(l.title)"
              @change="toggle(l.title)"
            />
            <span
              class="size-2.5 shrink-0 rounded-full"
              :style="{ backgroundColor: l.color }"
            />
            <span class="truncate">{{ l.title }}</span>
          </label>
          <p
            v-if="!filteredLabels.length"
            class="px-2 py-2 text-xs text-n-slate-10"
          >
            {{ $t('LABEL_MGMT.NO_RESULTS') }}
          </p>
        </div>
        <div
          v-if="modelValue.length"
          class="flex justify-end border-t border-n-weak pt-1"
        >
          <NextButton
            :label="$t('FILTER.CLEAR_BUTTON_LABEL')"
            variant="link"
            color="ruby"
            size="xs"
            @click="clearAll"
          />
        </div>
      </div>
    </div>

    <Teleport v-if="variant !== 'row' && isOpen" :to="teleportTarget">
      <div
        id="conversation-label-filter-panel"
        ref="panelRef"
        data-testid="conversation-label-filter-panel"
        class="pointer-events-auto flex w-full min-w-0 flex-col gap-2 rounded-2xl border border-n-weak bg-n-solid-1 p-2 shadow-sm"
        @keydown.esc="close"
      >
        <input
          v-model="search"
          type="text"
          :aria-label="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
          :placeholder="$t('LABEL_MGMT.SEARCH_PLACEHOLDER')"
          class="reset-base !mb-0 min-w-0 h-8 w-full appearance-none rounded-md border border-n-weak bg-n-background px-2 py-1.5 text-sm text-n-slate-12"
        />
        <div class="flex max-h-64 flex-col gap-0.5 overflow-auto">
          <label
            v-for="l in filteredLabels"
            :key="l.id"
            class="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
          >
            <input
              type="checkbox"
              :checked="isSelected(l.title)"
              @change="toggle(l.title)"
            />
            <span
              class="size-2.5 shrink-0 rounded-full"
              :style="{ backgroundColor: l.color }"
            />
            <span class="truncate">{{ l.title }}</span>
          </label>
          <p
            v-if="!filteredLabels.length"
            class="px-2 py-2 text-xs text-n-slate-10"
          >
            {{ $t('LABEL_MGMT.NO_RESULTS') }}
          </p>
        </div>
        <div
          v-if="modelValue.length"
          class="flex justify-end border-t border-n-weak pt-1"
        >
          <NextButton
            :label="$t('FILTER.CLEAR_BUTTON_LABEL')"
            variant="link"
            color="ruby"
            size="xs"
            @click="clearAll"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>
